import { pushAboAnlegen, pushAboLoeschen } from "@/api";

/**
 * Web Push im Browser (CHAT-AUFTRAG.md Stufe 3): Service Worker
 * registrieren, Erlaubnis holen, das Abo dieses Geräts beim Backend
 * hinterlegen. Alles hier ist Browser-Seiteneffekt — die Profil-Karte ruft
 * es, sonst niemand.
 *
 * iPhone/iPad: Push gibt es nur in der Startbildschirm-App (Safari → Teilen
 * → Zum Home-Bildschirm); im normalen Safari-Tab fehlt `PushManager`.
 */

export type PushLage = "nicht_unterstuetzt" | "ios_tab" | "verweigert" | "aus" | "an";

function istIos(): boolean {
	return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/** Läuft die Seite als installierte App (Startbildschirm, PWA-Fenster)? */
export function istStartbildschirmApp(): boolean {
	const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
	return standalone || window.matchMedia("(display-mode: standalone)").matches;
}

export function pushUnterstuetzt(): boolean {
	return (
		typeof window !== "undefined" &&
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}

export async function serviceWorkerRegistrieren(): Promise<ServiceWorkerRegistration | null> {
	if (!("serviceWorker" in navigator)) return null;
	try {
		return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
	} catch {
		return null;
	}
}

async function bestehendesAbo(): Promise<PushSubscription | null> {
	const registrierung = await navigator.serviceWorker.getRegistration("/");
	if (registrierung === undefined) return null;
	return registrierung.pushManager.getSubscription();
}

/** Wie steht Push auf DIESEM Gerät? */
export async function pushLage(): Promise<{ lage: PushLage; endpoint: string | null }> {
	if (!pushUnterstuetzt()) {
		return {
			lage: istIos() && !istStartbildschirmApp() ? "ios_tab" : "nicht_unterstuetzt",
			endpoint: null,
		};
	}
	if (Notification.permission === "denied") return { lage: "verweigert", endpoint: null };
	const abo = await bestehendesAbo();
	return abo === null ? { lage: "aus", endpoint: null } : { lage: "an", endpoint: abo.endpoint };
}

function base64UrlZuBytes(wert: string): Uint8Array<ArrayBuffer> {
	const padding = "=".repeat((4 - (wert.length % 4)) % 4);
	const base64 = (wert + padding).replace(/-/g, "+").replace(/_/g, "/");
	const roh = atob(base64);
	const bytes = new Uint8Array(new ArrayBuffer(roh.length));
	for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
	return bytes;
}

function schluesselAls(abo: PushSubscription, name: "p256dh" | "auth"): string {
	const roh = abo.getKey(name);
	if (roh === null) throw new Error("Der Browser hat keinen Push-Schlüssel geliefert.");
	return btoa(String.fromCharCode(...new Uint8Array(roh)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

/** Erlaubnis holen, abonnieren, beim Backend hinterlegen. */
export async function pushAktivieren(args: { publicKey: string }): Promise<PushLage> {
	if (!pushUnterstuetzt()) return "nicht_unterstuetzt";
	const erlaubnis = await Notification.requestPermission();
	if (erlaubnis !== "granted") return "verweigert";
	const registrierung =
		(await serviceWorkerRegistrieren()) ?? (await navigator.serviceWorker.ready);
	const abo =
		(await registrierung.pushManager.getSubscription()) ??
		(await registrierung.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: base64UrlZuBytes(args.publicKey),
		}));
	await pushAboAnlegen({
		endpoint: abo.endpoint,
		p256dh: schluesselAls(abo, "p256dh"),
		auth: schluesselAls(abo, "auth"),
		userAgent: navigator.userAgent.slice(0, 300),
	});
	return "an";
}

/** Abo dieses Geräts lösen — im Browser und beim Backend. */
export async function pushDeaktivieren(): Promise<void> {
	const abo = await bestehendesAbo();
	if (abo === null) return;
	await pushAboLoeschen({ endpoint: abo.endpoint }).catch(() => undefined);
	await abo.unsubscribe();
}
